import { Character, CharacterNameValues, CharacterData } from './Character';
import { app } from './core/App';
import { LevelName, LevelNameValues } from './level/levels/level';

export interface GameData {
  [GameDataHandler.keys.HighScore]?: number;
  [GameDataHandler.keys.LevelHighScores]?: Partial<Record<LevelNameValues, number>>;
  [GameDataHandler.keys.EarnedPoints]?: number;
  [GameDataHandler.keys.CharacterData]?: Partial<Record<CharacterNameValues, CharacterData>>;
  [GameDataHandler.keys.PurchasedLevels]?: LevelNameValues[];
  [GameDataHandler.keys.UnlockedLevels]?: LevelNameValues[];
  [GameDataHandler.keys.JustUnlockedLevels]?: LevelNameValues[];
}

export class GameDataHandler {
  public static keys = {
    Score: 'score',
    HighScore: 'highScore',
    LevelHighScores: 'levelHighScores',
    Distance: 'distance',
    EarnedPoints: 'earnedPoints',
    CharacterData: 'characterData',
    PurchasedLevels: 'purchasedLevels',
    UnlockedLevels: 'unlockedLevels',
    JustUnlockedLevels: 'justUnlockedLevels'
  } as const;
  public static events = {
    ChangeScore: `changedata-${GameDataHandler.keys.Score}`,
    ChangeHighScore: `changedata-${GameDataHandler.keys.HighScore}`,
    ChangeLevelHighScores: `changedata-${GameDataHandler.keys.LevelHighScores}`,
    ChangeDistance: `changedata-${GameDataHandler.keys.Distance}`,
    ChangeEarnedPoints: `changedata-${GameDataHandler.keys.EarnedPoints}`,
    ChangeCharacterData: `changedata-${GameDataHandler.keys.CharacterData}`,
    ChangePurchasedLevels: `changedata-${GameDataHandler.keys.PurchasedLevels}`,
    ChangeUnlockedLevels: `changedata-${GameDataHandler.keys.UnlockedLevels}`,
    ChangeJustUnlockedLevels: `changedata-${GameDataHandler.keys.JustUnlockedLevels}`
  } as const;

  private gameData: GameData;

  constructor(gameData: GameData) {
    // Set any defaults for data we're storing in gameData here.
    const defaultData: GameData = {
      [GameDataHandler.keys.HighScore]: 0,
      [GameDataHandler.keys.LevelHighScores]: {},
      [GameDataHandler.keys.EarnedPoints]: 0,
      [GameDataHandler.keys.CharacterData]: {},
      [GameDataHandler.keys.UnlockedLevels]: [LevelName.Level1],
      [GameDataHandler.keys.JustUnlockedLevels]: []
    };

    this.gameData = Object.assign({}, defaultData, gameData);
  }

  public static getHighScore(scene: Phaser.Scene, level: LevelNameValues) {
    let levelHighScores: Partial<Record<LevelNameValues, number>> = scene.game.registry.get(GameDataHandler.keys.LevelHighScores);
    let levelHighScore = levelHighScores[level];

    // Might be first play so we won't have a highScore.
    if (!levelHighScore) levelHighScore = 0;

    return levelHighScore;
  }

  public static setHighScore(scene: Phaser.Scene, level: LevelNameValues, newScore: number) {
    let levelHighScores: Partial<Record<LevelNameValues, number>> = scene.game.registry.get(GameDataHandler.keys.LevelHighScores);
    let levelHighScore = levelHighScores[level];

    // Might be first play so we won't have a highScore.
    if (!levelHighScore) levelHighScore = 0;

    if (newScore > levelHighScore) {
      levelHighScores[level] = newScore;
      scene.game.registry.set(GameDataHandler.keys.LevelHighScores, levelHighScores);
    }
  }

  public static canPurchase(scene: Phaser.Scene, purchaseAmount: number): boolean {
    let earnedPoints = scene.game.registry.get(GameDataHandler.keys.EarnedPoints);
    return earnedPoints >= purchaseAmount;
  }

  public static spendEarnedPoints(scene: Phaser.Scene, amountToSpend: number) {
    scene.game.registry.inc(GameDataHandler.keys.EarnedPoints, -amountToSpend);
  }

  public static addTopSpeedUpgrade(scene: Phaser.Scene, characterName: CharacterNameValues) {
    let characterData: Partial<Record<CharacterNameValues, CharacterData>> = scene.game.registry.get(GameDataHandler.keys.CharacterData);

    // Ensure a record exists for the character.
    if (!Object.hasOwn(characterData, characterName)) {
      characterData[characterName] = {
        topSpeedUpgrades: 0,
        accelerationUpgrades: 0
      };
    }

    if ((characterData[characterName] as CharacterData).topSpeedUpgrades < Character.maxTopSpeedUpgrades)
      (characterData[characterName] as CharacterData).topSpeedUpgrades++;

    scene.game.registry.set(GameDataHandler.keys.CharacterData, characterData);
  }

  public static addAccelerationUpgrade(scene: Phaser.Scene, characterName: CharacterNameValues) {
    let characterData: Partial<Record<CharacterNameValues, CharacterData>> = scene.game.registry.get(GameDataHandler.keys.CharacterData);

    // Ensure a record exists for the character.
    if (!Object.hasOwn(characterData, characterName)) {
      characterData[characterName] = {
        topSpeedUpgrades: 0,
        accelerationUpgrades: 0
      };
    }

    if ((characterData[characterName] as CharacterData).accelerationUpgrades < Character.maxAccelerationUpgrades)
      (characterData[characterName] as CharacterData).accelerationUpgrades++;

    scene.game.registry.set(GameDataHandler.keys.CharacterData, characterData);
  }

  public static getCharacterData(scene: Phaser.Scene, characterName: CharacterNameValues): CharacterData {
    let characterData: Partial<Record<CharacterNameValues, CharacterData>> = scene.game.registry.get(GameDataHandler.keys.CharacterData);

    // Ensure a record exists for the character.
    if (!Object.hasOwn(characterData, characterName)) {
      characterData[characterName] = {
        topSpeedUpgrades: 0,
        accelerationUpgrades: 0
      };
    }

    return (characterData as Record<CharacterNameValues, CharacterData>)[characterName];
  }

  public static isLevelUnlockedButNotPurchased(scene: Phaser.Scene, levelId: LevelNameValues) {
    let unlockedLevels: LevelNameValues[] = scene.game.registry.get(GameDataHandler.keys.UnlockedLevels);
    //let purchasedLevels: LevelNameValues[] = scene.game.registry.get(GameDataHandler.keys.PurchasedLevels);

    return unlockedLevels.indexOf(levelId) !== -1;// && purchasedLevels.indexOf(levelId) === -1;
  }

  public static isLevelUnlocked(scene: Phaser.Scene, levelId: LevelNameValues): boolean {
    let unlockedLevels: LevelNameValues[] = scene.game.registry.get(GameDataHandler.keys.UnlockedLevels);
    return unlockedLevels.indexOf(levelId) !== -1;
  }

  public static isLevelPurchased(scene: Phaser.Scene, levelId: LevelNameValues): boolean {
    // let purchasedLevels: LevelNameValues[] = scene.game.registry.get(GameDataHandler.keys.PurchasedLevels);
    // return purchasedLevels.indexOf(levelId) !== -1;
    return true;
  }

  public static purchaseLevel(scene: Phaser.Scene, purchasedLevel: LevelNameValues) {
    // let purchasedLevels: LevelNameValues[] = scene.game.registry.get(GameDataHandler.keys.PurchasedLevels);

    // if (purchasedLevels.indexOf(purchasedLevel) == -1) {
    //   purchasedLevels.push(purchasedLevel);
    //   scene.game.registry.set(GameDataHandler.keys.PurchasedLevels, purchasedLevels);
    // }
  }

  public static unlockLevel(scene: Phaser.Scene, unlockedLevel: LevelNameValues) {
    let unlockedLevels: LevelNameValues[] = scene.game.registry.get(GameDataHandler.keys.UnlockedLevels);
    let justUnlockedLevels: LevelNameValues[] = scene.game.registry.get(GameDataHandler.keys.JustUnlockedLevels);

    if (unlockedLevels.indexOf(unlockedLevel) == -1) {
      unlockedLevels.push(unlockedLevel);
      scene.game.registry.set(GameDataHandler.keys.UnlockedLevels, unlockedLevels);

      if (justUnlockedLevels.indexOf(unlockedLevel) == -1) {
        justUnlockedLevels.push(unlockedLevel);
        scene.game.registry.set(GameDataHandler.keys.JustUnlockedLevels, justUnlockedLevels);
      }
    }
  }

  public static clearJustUnlockedLevels(scene: Phaser.Scene) {
    scene.game.registry.set(GameDataHandler.keys.JustUnlockedLevels, []);
  }

  // Apply default values that we don't want saved in gameData.
  public applyDefaultsToPhaserRegistry(game: Phaser.Game) {
    game.registry.set({
      score: 0,
      distance: 0
    });
  }

  // Apply any loaded values or sane defaults for non-loaded savable values.
  public applySavedDataToPhaserRegistry(game: Phaser.Game) {
    game.registry.set(this.gameData);
  }

  // Bind all the events for data changes.
  public bindToPhaserRegistry(game: Phaser.Game) {
    game.registry.events.on(GameDataHandler.events.ChangeHighScore, this.onChangeHighScore, this);
    game.registry.events.on(GameDataHandler.events.ChangeLevelHighScores, this.onChangeLevelHighScore, this);
    game.registry.events.on(GameDataHandler.events.ChangeEarnedPoints, this.onChangeEarnedPoints, this);
    game.registry.events.on(GameDataHandler.events.ChangeCharacterData, this.onChangeCharacterData, this);
    game.registry.events.on(GameDataHandler.events.ChangePurchasedLevels, this.onLevelPurchased, this);
    game.registry.events.on(GameDataHandler.events.ChangeUnlockedLevels, this.onLevelUnlocked, this);
    game.registry.events.on(GameDataHandler.events.ChangeJustUnlockedLevels, this.onLevelJustUnlocked, this);
  }

  protected onChangeHighScore(parent: any, value: any, previousValue: any) {
    this.gameData[GameDataHandler.keys.HighScore] = value;
    app.dataHandler.save(this.gameData);
  }

  protected onChangeLevelHighScore(parent: any, value: any, previousValue: any) {
    this.gameData[GameDataHandler.keys.LevelHighScores] = value;
    app.dataHandler.save(this.gameData);
  }

  protected onChangeEarnedPoints(parent: any, value: any, previousValue: any) {
    this.gameData[GameDataHandler.keys.EarnedPoints] = value;
    app.dataHandler.save(this.gameData);
  }

  protected onChangeCharacterData(parent: any, value: any, previousValue: any) {
    this.gameData[GameDataHandler.keys.CharacterData] = value;
    app.dataHandler.save(this.gameData);
  }

  protected onLevelPurchased(parent: any, value: any, previousValue: any) {
    this.gameData[GameDataHandler.keys.PurchasedLevels] = value;
    app.dataHandler.save(this.gameData);
  }

  protected onLevelUnlocked(parent: any, value: any, previousValue: any) {
    this.gameData[GameDataHandler.keys.UnlockedLevels] = value;
    app.dataHandler.save(this.gameData);
  }

  protected onLevelJustUnlocked(parent: any, value: any, previousValue: any) {
    this.gameData[GameDataHandler.keys.JustUnlockedLevels] = value;
    app.dataHandler.save(this.gameData);
  }
}
